import { 
  IconButton, 
  Tooltip, 
  useToast, 
  useDisclosure, 
  HStack, 
  Text, 
  Modal, 
  ModalOverlay, 
  ModalContent, 
  ModalBody, 
  VStack, 
  Box
} from '@chakra-ui/react';
import { FiPlus, FiFolder, FiLink } from 'react-icons/fi';
import { FaDropbox } from 'react-icons/fa';
import { useState, useRef, useCallback, useEffect } from 'react';
import { DropboxService } from '../services/DropboxService';
import { GoogleDriveService } from '../services/GoogleDriveService';
import TooltipKeyBox from './TooltipKeyBox';
import MobileDownloadWarning from './MobileDownloadWarning';
import { getMobileDownloadWarnings, isMobileDevice } from '../utils/deviceDetection';
import DirectFileLinkImportModal from './DirectFileLinkImportModal';

// Extend Window interface to include Dropbox and Google Picker
declare global {
  interface Window {
    Dropbox?: {
      choose: (options: DropboxChooseOptions) => void;
    };
    gapi?: {
      load: (api: string, callback: { callback: () => void }) => void;
    };
    google?: {
      picker: {
        ViewId: {
          DOCS: string;
        };
        Feature: {
          NAV_HIDDEN: string;
          MULTISELECT_ENABLED: string;
        };
        DocsViewMode: {
          LIST: string;
          GRID: string;
        };
        View: new (viewId: string) => {
          setMimeTypes: (mimeTypes: string) => void;
        };
        DocsView: new () => {
          setMimeTypes: (mimeTypes: string) => any;
          setIncludeFolders: (include: boolean) => any;
          setMode: (mode: string) => any;
        };
        PickerBuilder: new () => {
          enableFeature: (feature: string) => any;
          setOAuthToken: (token: string) => any;
          addView: (view: any) => any;
          setCallback: (callback: (data: any) => void) => any;
          build: () => {
            setVisible: (visible: boolean) => void;
          };
        };
        Action: {
          PICKED: string;
          CANCEL: string;
        };
      };
    };
  }
}

// Define types for Dropbox file objects
interface DropboxFile {
  id: string;
  name: string;
  link: string;
  bytes: number;
  icon: string;
  isDir?: boolean;
  thumbnailLink?: string;
}

// Define types for Google Drive file objects
interface GoogleDriveFile {
  id: string;
  name: string;
  url?: string;
  sizeBytes?: number;
  mimeType?: string;
}

// Define types for Dropbox chooser options
interface DropboxChooseOptions {
  success: (files: DropboxFile[]) => void;
  cancel?: () => void;
  linkType?: 'preview' | 'direct';
  multiselect?: boolean;
  filter?: { extensions: string[] };
  folderselect?: boolean;
  sizeLimit?: number;
}

interface AddFilesButtonProps {
  onFilesSelected: (files: File[], urls: string[], shareableUrls: string[]) => void;
  loadedFiles: Array<{ shareableUrl?: string }>;
  onLoadingChange?: (isLoading: boolean) => void;
  onProgressChange?: (downloadedBytes: number, totalBytes: number, completedFiles?: number, totalFiles?: number) => void;
}

const AddFilesButton: React.FC<AddFilesButtonProps> = ({ onFilesSelected, loadedFiles, onLoadingChange, onProgressChange }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [downloadedBytes, setDownloadedBytes] = useState(0);
  const [totalBytes, setTotalBytes] = useState(0);
  const [completedFiles, setCompletedFiles] = useState(0);
  const [totalFiles, setTotalFiles] = useState(0);
  const [pendingFiles, setPendingFiles] = useState<DropboxFile[] | GoogleDriveFile[]>([]);
  const [pendingFilesType, setPendingFilesType] = useState<'dropbox' | 'googledrive' | null>(null);
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const modalContainerRef = useRef<HTMLDivElement>(null);
  const { isOpen: isAddFilesModalOpen, onOpen: onAddFilesModalOpen, onClose: onAddFilesModalClose } = useDisclosure();
  const { isOpen: isDirectLinkModalOpen, onOpen: onDirectLinkModalOpen, onClose: onDirectLinkModalClose } = useDisclosure();

  // Memoize the loading change callback to prevent stale closures
  const handleDirectLinkLoadingChange = useCallback((loading: boolean) => {
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/10a482d6-3b60-4d80-a61c-e7d18febb305',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'AddFilesButton.tsx:974',message:'DirectFileLinkImportModal onLoadingChange callback called',data:{loading},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'G'})}).catch(()=>{});
    // #endregion
    setIsLoading(loading);
  }, []);

  // Memoize the progress change callback to prevent stale closures
  const handleDirectLinkProgressChange = useCallback((downloaded: number, total: number, completedFilesCount?: number, totalFilesCount?: number) => {
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/10a482d6-3b60-4d80-a61c-e7d18febb305',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'AddFilesButton.tsx:978',message:'DirectFileLinkImportModal onProgressChange callback called',data:{downloaded,total,completedFilesCount,totalFilesCount},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'H'})}).catch(()=>{});
    // #endregion
    // Update state - the useEffect will notify the parent
    setDownloadedBytes(downloaded);
    setTotalBytes(total);
    setCompletedFiles(completedFilesCount || 0);
    setTotalFiles(totalFilesCount || 0);
  }, []);

  // Notify parent of loading state changes
  useEffect(() => {
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/10a482d6-3b60-4d80-a61c-e7d18febb305',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'AddFilesButton.tsx:126',message:'AddFilesButton loading state changed',data:{isLoading,hasCallback:!!onLoadingChange},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'D'})}).catch(()=>{});
    // #endregion
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  // Notify parent of progress changes
  useEffect(() => {
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/10a482d6-3b60-4d80-a61c-e7d18febb305',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'AddFilesButton.tsx:131',message:'AddFilesButton progress changed',data:{downloadedBytes,totalBytes,completedFiles,totalFiles,hasCallback:!!onProgressChange},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'E'})}).catch(()=>{});
    // #endregion
    onProgressChange?.(downloadedBytes, totalBytes, completedFiles, totalFiles);
  }, [downloadedBytes, totalBytes, completedFiles, totalFiles, onProgressChange]);
  
  // Mobile warning state
  const { 
    isOpen: isMobileWarningOpen, 
    onOpen: onMobileWarningOpen, 
    onClose: onMobileWarningClose 
  } = useDisclosure();

  // Blur all buttons and focus modal container when modal opens
  useEffect(() => {
    if (isAddFilesModalOpen) {
      // Blur any currently focused element
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      // Focus the modal container after a short delay to ensure it's rendered
      setTimeout(() => {
        if (modalContainerRef.current) {
          modalContainerRef.current.focus();
        }
      }, 0);
    }
  }, [isAddFilesModalOpen]);

  // Detect if current files are from Dropbox or Google Drive
  const hasDropboxFiles = loadedFiles.some(file => 
    file.shareableUrl && 
    (file.shareableUrl.includes('dropbox.com') || file.shareableUrl.includes('dropboxusercontent.com'))
  );
  
  const hasGoogleDriveFiles = loadedFiles.some(file => 
    file.shareableUrl && 
    (file.shareableUrl.includes('drive.google.com') || file.shareableUrl.includes('googleusercontent.com'))
  );

  // Check if any files are local (not shareable)
  const hasLocalFiles = loadedFiles.some(file => {
    const shareableUrl = file.shareableUrl;
    return !shareableUrl || shareableUrl === '' || shareableUrl.length === 0;
  });

  // Check if only cloud-hosted files are present (gdrive/dropbox)
  const onlyCloudFiles = !hasLocalFiles && (hasDropboxFiles || hasGoogleDriveFiles);

  // Get dropbox service safely
  const getDropboxService = useCallback(() => {
    try {
      return DropboxService.getInstance();
    } catch (error) {
      console.error("[AddFilesButton] Failed to get DropboxService instance:", error);
      return null;
    }
  }, []);

  // Get Google Drive service safely
  const getGoogleDriveService = useCallback(() => {
    try {
      return GoogleDriveService.getInstance();
    } catch (error) {
      console.error("[AddFilesButton] Failed to get GoogleDriveService instance:", error);
      return null;
    }
  }, []);

  const loadDropboxSDK = (): Promise<void> => {
    return new Promise((resolve) => {
      if (window.Dropbox) return resolve();
      
      const script = document.createElement('script');
      script.src = 'https://www.dropbox.com/static/api/2/dropins.js';
      script.id = 'dropboxjs';
      script.setAttribute('data-app-key', '7avzllilqvi9a9p');
      script.onload = () => resolve();
      script.onerror = () => {
        console.error('[AddFilesButton] Failed to load Dropbox SDK');
        resolve(); // Resolve anyway to avoid hanging
      };
      document.body.appendChild(script);
    });
  };

  const loadGooglePickerAPI = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (window.gapi && window.google) {
        resolve();
        return;
      }

      if (!document.getElementById('gapi-script')) {
        const script = document.createElement('script');
        script.src = 'https://apis.google.com/js/api.js';
        script.id = 'gapi-script';
        script.onload = () => {
          if (window.gapi) {
            window.gapi.load('picker', {
              callback: () => resolve()
            });
          } else {
            reject(new Error('Failed to load Google API'));
          }
        };
        script.onerror = () => {
          console.error('[AddFilesButton] Failed to load Google API');
          reject(new Error('Failed to load Google API'));
        };
        document.body.appendChild(script);
      } else {
        const checkInterval = setInterval(() => {
          if (window.gapi && window.google) {
            clearInterval(checkInterval);
            resolve();
          }
        }, 100);
        
        setTimeout(() => {
          clearInterval(checkInterval);
          reject(new Error('Timeout waiting for Google API'));
        }, 10000);
      }
    });
  };

  const handleLocalFileSelect = () => {
    inputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      onFilesSelected(files, files.map(() => ''), files.map(() => ''));
    }
  };

  const openDropboxPicker = useCallback(async () => {
    const dropboxService = getDropboxService();
    if (!dropboxService) {
      toast({
        title: "Dropbox Service Error",
        description: "Failed to initialize Dropbox service",
        status: "error",
        duration: 5000,
        isClosable: true
      });
      return;
    }
    
    await loadDropboxSDK();
    if (!window.Dropbox) {
      toast({
        title: "Dropbox SDK Error",
        description: "Failed to load Dropbox SDK",
        status: "error",
        duration: 5000,
        isClosable: true
      });
      return;
    }

    window.Dropbox.choose({
      success: async (files: DropboxFile[]) => {
        try {
          console.log(`[AddFilesButton] Processing ${files.length} files`);
          
          if (!dropboxService.isAuthenticated()) {
            localStorage.setItem('dropboxAuthInProgress', '1');
            await dropboxService.authenticate();
            return;
          }

          // Calculate total bytes for mobile warnings
          const totalFileBytes = files.reduce((sum, file) => sum + file.bytes, 0);
          console.log(`[AddFilesButton] Total download size: ${(totalFileBytes / 1024 / 1024).toFixed(2)} MB`);
          
          // Check for mobile warnings
          const warnings = getMobileDownloadWarnings(totalFileBytes);
          console.log('[AddFilesButton] Mobile warnings:', warnings);
          
          if (warnings.shouldWarnDataUsage || warnings.shouldWarnLargeSize) {
            // Store files and show warning modal
            setPendingFiles(files);
            setPendingFilesType('dropbox');
            setTotalBytes(totalFileBytes);
            onMobileWarningOpen();
            return;
          }
          
          // Set loading state BEFORE processing files so spinner shows immediately
          setIsLoading(true);
          
          // Proceed with download directly if no warnings
          await processFiles(files, dropboxService);
        } catch (error: unknown) {
          console.error('[AddFilesButton] Error processing files:', error);
          toast({
            title: 'Error Importing Files',
            description: error instanceof Error ? error.message : 'Failed to import files from Dropbox',
            status: 'error',
            duration: 9000,
            isClosable: true
          });
        }
      },
      cancel: () => setIsLoading(false),
      linkType: "preview",
      multiselect: true,
      filter: { extensions: ['mp3', 'wav', 'm4a', 'aac', 'ogg', 'flac'] }
    });
  }, [getDropboxService, onFilesSelected, toast]);

  // Process Google Drive files for download with progress tracking
  const processGoogleDriveFiles = useCallback(async (files: GoogleDriveFile[], googleDriveService: GoogleDriveService) => {
    // Note: isLoading is set by the caller before this function is called
    setDownloadedBytes(0);
    
    // Calculate total bytes upfront from file metadata
    const totalFileBytes = files.reduce((sum, file) => sum + (file.sizeBytes || 0), 0);
    setTotalBytes(totalFileBytes);
    console.log(`[AddFilesButton] Total download size: ${(totalFileBytes / 1024 / 1024).toFixed(2)} MB`);
    
    try {
      const fileLinks = files.map(file => ({
        id: file.id,
        name: file.name,
        link: file.url || `https://drive.google.com/file/d/${file.id}/view`
      }));

      const permanentLinks = await googleDriveService.getPermanentLinks(fileLinks);

      console.log(`[AddFilesButton] Generated ${permanentLinks.length} permanent links`);

      // Track individual file progress
      const fileProgress = new Map<string, { downloaded: number; total: number }>();
      
      const results = await Promise.all(
        permanentLinks.map(async (link, index) => {
          const file = files[index];
          try {
            const blob = await googleDriveService.getFileContent(
              file.id, 
              link.url,
              (downloaded, total) => {
                // Update individual file progress
                fileProgress.set(file.id, { downloaded, total });
                
                // Calculate total downloaded bytes across all files
                let totalDownloaded = 0;
                
                files.forEach(f => {
                  const progress = fileProgress.get(f.id);
                  if (progress && progress.downloaded > 0) {
                    // File is being downloaded, add its downloaded bytes
                    totalDownloaded += progress.downloaded;
                  }
                });
                
                // Update downloaded bytes (totalBytes was already set upfront)
                setDownloadedBytes(totalDownloaded);
              }
            );
            
            let mimeType = blob.type || file.mimeType;
            if (!mimeType || mimeType === 'application/octet-stream') {
              const extension = file.name.toLowerCase().split('.').pop();
              switch (extension) {
                case 'mp3': mimeType = 'audio/mpeg'; break;
                case 'wav': mimeType = 'audio/wav'; break;
                case 'm4a': mimeType = 'audio/mp4'; break;
                case 'aac': mimeType = 'audio/aac'; break;
                case 'ogg': mimeType = 'audio/ogg'; break;
                case 'flac': mimeType = 'audio/flac'; break;
                default: mimeType = 'audio/mpeg';
              }
            }
            
            const shareableUrl = (link as any).shareableUrl || `https://drive.google.com/file/d/${file.id}/view`;
            
            return {
              file: new File([blob], file.name, { type: mimeType }),
              originalUrl: file.url || `https://drive.google.com/file/d/${file.id}/view`,
              shareableUrl
            };
          } catch (fileError) {
            console.error(`[AddFilesButton] Failed to process file: ${file.name}`, fileError);
            throw fileError;
          }
        })
      );

      onFilesSelected(
        results.map(r => r.file),
        results.map(r => r.originalUrl),
        results.map(r => r.shareableUrl)
      );
    } catch (error: unknown) {
      console.error('[AddFilesButton] Error processing Google Drive files:', error);
      toast({
        title: 'Error Importing Files',
        description: error instanceof Error ? error.message : 'Failed to import files from Google Drive',
        status: 'error',
        duration: 9000,
        isClosable: true
      });
    } finally {
      setIsLoading(false);
      setDownloadedBytes(0);
      setTotalBytes(0);
    }
  }, [onFilesSelected, toast]);

  // Process files for download with progress tracking
  const processFiles = useCallback(async (files: DropboxFile[], dropboxService: DropboxService) => {
    // Note: isLoading is set by the caller before this function is called
    setDownloadedBytes(0);
    
    // Calculate total bytes upfront from file metadata
    const totalFileBytes = files.reduce((sum, file) => sum + (file.bytes || 0), 0);
    setTotalBytes(totalFileBytes);
    console.log(`[AddFilesButton] Total download size: ${(totalFileBytes / 1024 / 1024).toFixed(2)} MB`);
    
    try {
      const permanentLinks = await dropboxService.getPermanentLinks(
        files.map(file => ({ id: file.id, name: file.name, link: file.link }))
      );

      console.log(`[AddFilesButton] Generated ${permanentLinks.length} permanent links`);

      // Track individual file progress
      const fileProgress = new Map<string, { downloaded: number; total: number }>();
      
      const results = await Promise.all(
        permanentLinks.map(async (link, index) => {
          const file = files[index];
          try {
            const blob = await dropboxService.getFileContent(
              file.id, 
              link.url,
              (downloaded, total) => {
                // Update individual file progress
                fileProgress.set(file.id, { downloaded, total });
                
                // Calculate total downloaded bytes across all files
                let totalDownloaded = 0;
                
                files.forEach(f => {
                  const progress = fileProgress.get(f.id);
                  if (progress && progress.downloaded > 0) {
                    // File is being downloaded, add its downloaded bytes
                    totalDownloaded += progress.downloaded;
                  }
                });
                
                // Update downloaded bytes (totalBytes was already set upfront)
                setDownloadedBytes(totalDownloaded);
                console.log(`[AddFilesButton] Progress: ${Math.round((totalDownloaded / totalFileBytes) * 100)}% (${totalDownloaded}/${totalFileBytes} bytes)`);
              }
            );
            
            console.log(`[AddFilesButton] Successfully processed: ${file.name}`);
            console.log(`[AddFilesButton] Blob type: "${blob.type}", size: ${blob.size} bytes`);
            
            // Ensure we have a proper MIME type for audio files
            let mimeType = blob.type;
            if (!mimeType || mimeType === 'application/octet-stream') {
              // Try to determine MIME type from file extension
              const extension = file.name.toLowerCase().split('.').pop();
              switch (extension) {
                case 'mp3':
                  mimeType = 'audio/mpeg';
                  break;
                case 'wav':
                  mimeType = 'audio/wav';
                  break;
                case 'm4a':
                  mimeType = 'audio/mp4';
                  break;
                case 'aac':
                  mimeType = 'audio/aac';
                  break;
                case 'ogg':
                  mimeType = 'audio/ogg';
                  break;
                case 'flac':
                  mimeType = 'audio/flac';
                  break;
                default:
                  mimeType = 'audio/mpeg'; // fallback
              }
              console.log(`[AddFilesButton] Inferred MIME type: "${mimeType}" for extension: ${extension}`);
            }
            
            return {
              file: new File([blob], file.name, { type: mimeType }),
              originalUrl: file.link,
              shareableUrl: link.url
            };
          } catch (fileError) {
            console.error(`[AddFilesButton] Failed to process file: ${file.name}`, fileError);
            throw fileError;
          }
        })
      );

      onFilesSelected(
        results.map(r => r.file),
        results.map(r => r.originalUrl),
        results.map(r => r.shareableUrl)
      );
    } catch (error: unknown) {
      console.error('[AddFilesButton] Error processing files:', error);
      toast({
        title: 'Error Importing Files',
        description: error instanceof Error ? error.message : 'Failed to import files from Dropbox',
        status: 'error',
        duration: 9000,
        isClosable: true
      });
    } finally {
      setIsLoading(false);
      setDownloadedBytes(0);
      setTotalBytes(0);
    }
  }, [onFilesSelected, toast]);

  // Handle mobile warning for additional files
  const handleMobileWarningContinue = async () => {
    if (pendingFiles.length > 0 && pendingFilesType) {
      onMobileWarningClose();
      // Set loading state BEFORE processing files so spinner shows immediately
      setIsLoading(true);
      if (pendingFilesType === 'dropbox') {
        const dropboxService = getDropboxService();
        if (dropboxService) {
          await processFiles(pendingFiles as DropboxFile[], dropboxService);
        }
      } else if (pendingFilesType === 'googledrive') {
        const googleDriveService = getGoogleDriveService();
        if (googleDriveService) {
          await processGoogleDriveFiles(pendingFiles as GoogleDriveFile[], googleDriveService);
        }
      }
      setPendingFiles([]);
      setPendingFilesType(null);
      setTotalBytes(0);
    }
  };

  const handleMobileWarningClose = () => {
    onMobileWarningClose();
    setPendingFiles([]);
    setPendingFilesType(null);
    setTotalBytes(0);
  };

  const openGoogleDrivePicker = useCallback(async () => {
    const googleDriveService = getGoogleDriveService();
    if (!googleDriveService) {
      toast({
        title: "Google Drive Service Error",
        description: "Failed to initialize Google Drive service",
        status: "error",
        duration: 5000,
        isClosable: true
      });
      return;
    }
    
    if (!googleDriveService.isAuthenticated()) {
      localStorage.setItem('googleDriveAuthInProgress', '1');
      await googleDriveService.authenticate();
      return;
    }

    try {
      await loadGooglePickerAPI();
      
      if (!window.google || !window.google.picker) {
        toast({
          title: "Google Picker API Error",
          description: "Failed to load Google Picker API",
          status: "error",
          duration: 5000,
          isClosable: true
        });
        return;
      }

      const accessToken = localStorage.getItem('googleDriveAccessToken');
      if (!accessToken) {
        toast({
          title: "Authentication Error",
          description: "Google Drive access token not found",
          status: "error",
          duration: 5000,
          isClosable: true
        });
        return;
      }

      const view = new window.google.picker.View(window.google.picker.ViewId.DOCS);
      view.setMimeTypes('audio/mpeg,audio/wav,audio/mp4,audio/aac,audio/ogg,audio/flac');

      if (!window.google || !window.google.picker) {
        throw new Error('Google Picker API not loaded');
      }

      const picker = new window.google.picker.PickerBuilder()
        .enableFeature(window.google.picker.Feature.NAV_HIDDEN)
        .enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED)
        .setOAuthToken(accessToken)
        .addView(view)
        .setCallback((data: any) => {
          if (!window.google || !window.google.picker) return;
          if (data.action === window.google.picker.Action.PICKED) {
            const files: GoogleDriveFile[] = data.docs.map((doc: any) => ({
              id: doc.id,
              name: doc.name,
              url: doc.url,
              sizeBytes: doc.sizeBytes ? parseInt(doc.sizeBytes, 10) : undefined,
              mimeType: doc.mimeType
            }));

            try {
              const totalFileBytes = files.reduce((sum, file) => sum + (file.sizeBytes || 0), 0);
              const warnings = getMobileDownloadWarnings(totalFileBytes);
              
              if (warnings.shouldWarnDataUsage || warnings.shouldWarnLargeSize) {
                setPendingFiles(files);
                setPendingFilesType('googledrive');
                setTotalBytes(totalFileBytes);
                onMobileWarningOpen();
                return;
              }
              
              // Set loading state BEFORE processing files so spinner shows immediately
              setIsLoading(true);
              
              processGoogleDriveFiles(files, googleDriveService);
            } catch (error: unknown) {
              console.error('[AddFilesButton] Error processing Google Drive files:', error);
              toast({
                title: 'Error Importing Files',
                description: error instanceof Error ? error.message : 'Failed to import files from Google Drive',
                status: 'error',
                duration: 9000,
                isClosable: true
              });
            }
          } else if (window.google && window.google.picker && data.action === window.google.picker.Action.CANCEL) {
            setIsLoading(false);
          }
        })
        .build();
      
      picker.setVisible(true);
    } catch (error) {
      console.error('[AddFilesButton] Error opening Google Drive picker:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to open Google Drive picker',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    }
  }, [getGoogleDriveService, toast, processGoogleDriveFiles, onMobileWarningOpen, loadGooglePickerAPI]);

  const handleDropboxSelect = async () => {
    const dropboxService = getDropboxService();
    if (!dropboxService) {
      toast({
        title: "Dropbox Service Error",
        description: "Failed to initialize Dropbox service",
        status: "error",
        duration: 5000,
        isClosable: true
      });
      return;
    }
    
    if (!dropboxService.isAuthenticated()) {
      localStorage.setItem('dropboxAuthInProgress', '1');
      await dropboxService.authenticate();
      return;
    }
    await openDropboxPicker();
  };

  const handleGoogleDriveSelect = async () => {
    await openGoogleDrivePicker();
  };

  const handleLocalFileSelectFromModal = () => {
    onAddFilesModalClose();
    handleLocalFileSelect();
  };

  const handleDropboxSelectFromModal = async () => {
    onAddFilesModalClose();
    await handleDropboxSelect();
  };

  const handleGoogleDriveSelectFromModal = () => {
    // Close modal first, then open picker
    // The picker must be opened in direct response to user interaction
    onAddFilesModalClose();
    // Use setTimeout to ensure modal closes first, then open picker
    setTimeout(() => {
      handleGoogleDriveSelect();
    }, 50);
  };

  const handleDirectLinkSelectFromModal = () => {
    onAddFilesModalClose();
    onDirectLinkModalOpen();
  };

  // Keyboard shortcuts for the modal
  useEffect(() => {
    if (!isAddFilesModalOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip hotkeys when user is editing text
      if (e.target instanceof HTMLInputElement || 
          e.target instanceof HTMLTextAreaElement ||
          (e.target instanceof HTMLElement && e.target.contentEditable === 'true')) {
        return;
      }

      // Only handle plain keys without modifiers (except Shift for capital letters)
      if (e.metaKey || e.ctrlKey || e.altKey) {
        return;
      }

      const key = e.key.toLowerCase();
      
      switch (key) {
        case 'l':
          e.preventDefault();
          e.stopPropagation();
          handleLocalFileSelectFromModal();
          break;
        case 'd':
          e.preventDefault();
          e.stopPropagation();
          handleDropboxSelectFromModal();
          break;
        case 'f':
          e.preventDefault();
          e.stopPropagation();
          handleDirectLinkSelectFromModal();
          break;
      }
    };

    // Use capture phase to ensure we catch it before other handlers
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [isAddFilesModalOpen, handleLocalFileSelectFromModal, handleDropboxSelectFromModal, handleDirectLinkSelectFromModal]);

  const handleClick = () => {
    onAddFilesModalOpen();
  };

  const tooltipContent = (
    <HStack spacing={2} color="gray.800">
      <Text>Add more files</Text>
      <TooltipKeyBox>I</TooltipKeyBox>
    </HStack>
  );

  return (
    <>
      <input
        type="file"
        ref={inputRef}
        onChange={handleFileChange}
        accept=".mp3,.wav,.ogg,.m4a,.aac,.flac"
        style={{ display: 'none' }}
        multiple
      />
      <Tooltip 
        label={
          isLoading && totalBytes > 0 && downloadedBytes > 0
            ? `Adding files... ${Math.round((downloadedBytes / totalBytes) * 100)}%`
            : isLoading && totalBytes > 0
            ? `Adding files... ${(totalBytes / 1024 / 1024).toFixed(0)}MB`
            : tooltipContent
        } 
        placement="top"
      >
        <IconButton
          data-testid="add-files-button"
          aria-label="Add more files"
          icon={<FiPlus size={36} />}
          onClick={handleClick}
          isLoading={false}
          variant="ghost"
          size="lg"
          width="72px"
          height="72px"
          colorScheme="blue"
          _hover={{ 
            color: "accent.400",
            transform: 'scale(1.05)',
            borderColor: 'transparent'
          }}
          _active={{ 
            color: "accent.500",
            transform: 'scale(0.95)',
            borderColor: 'transparent'
          }}
          _focus={{ boxShadow: "none" }}
          _focusVisible={{ boxShadow: "none" }}
        />
      </Tooltip>
      
      {/* Mobile Download Warning Modal */}
      <MobileDownloadWarning
        isOpen={isMobileWarningOpen}
        onClose={handleMobileWarningClose}
        onContinue={handleMobileWarningContinue}
        sizeMB={Math.round(totalBytes / (1024 * 1024))}
        shouldWarnDataUsage={pendingFiles.length > 0 && getMobileDownloadWarnings(totalBytes).shouldWarnDataUsage}
        shouldWarnLargeSize={pendingFiles.length > 0 && getMobileDownloadWarnings(totalBytes).shouldWarnLargeSize}
        networkType={pendingFiles.length > 0 ? getMobileDownloadWarnings(totalBytes).networkInfo.effectiveType : 'unknown'}
      />
      
      {/* Add Files Selection Modal */}
      <Modal isOpen={isAddFilesModalOpen} onClose={onAddFilesModalClose} isCentered size={{ base: "sm", md: "md" }}>
        <ModalOverlay />
        <ModalContent bg="transparent" maxW="2xl" boxShadow="none">
          <ModalBody p={0}>
            <VStack spacing={6} align="center">
              <Box
                ref={modalContainerRef}
                position="relative"
                bg="gray.900"
                borderRadius="2xl"
                pt={2}
                pb={2}
                px={4}
                maxW="600px"
                border="2px solid"
                borderColor="#FFD700"
                boxShadow="0 20px 40px rgba(0, 0, 0, 0.4), 0 0 20px rgba(255, 152, 0, 0.15)"
                tabIndex={-1}
                outline="none"
                sx={{
                  animation: 'twinkleBorder 3s ease-in-out infinite',
                  '@keyframes twinkleBorder': {
                    '0%, 100%': {
                      borderColor: '#FFD700',
                      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4), 0 0 20px rgba(255, 215, 0, 0.3), 0 0 30px rgba(255, 107, 107, 0.2)',
                    },
                    '33%': {
                      borderColor: '#FF6B6B',
                      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4), 0 0 20px rgba(255, 107, 107, 0.3), 0 0 30px rgba(78, 205, 196, 0.2)',
                    },
                    '66%': {
                      borderColor: '#4ECDC4',
                      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4), 0 0 20px rgba(78, 205, 196, 0.3), 0 0 30px rgba(255, 215, 0, 0.2)',
                    },
                  },
                }}
              >
                <HStack spacing={2} justify="center" alignItems="center">
                  <Tooltip 
                    label={
                      <HStack spacing={2} color="gray.800">
                        <Text>Import local files</Text>
                        <TooltipKeyBox>L</TooltipKeyBox>
                      </HStack>
                    } 
                    placement="top"
                    offset={[0, -35]}
                  >
                    <IconButton
                      aria-label="Add local files"
                      icon={<FiFolder size={80} />}
                      onClick={handleLocalFileSelectFromModal}
                      colorScheme="blue"
                      variant="ghost"
                      size="lg"
                      width="150px"
                      height="150px"
                      borderRadius="2xl"
                      _hover={{
                        transform: 'scale(1.05)',
                        borderColor: 'transparent'
                      }}
                      _active={{
                        transform: 'scale(0.95)',
                        borderColor: 'transparent'
                      }}
                    />
                  </Tooltip>

                  <Tooltip 
                    label={
                      <HStack spacing={2} color="gray.800">
                        <Text>Import from Dropbox</Text>
                        <TooltipKeyBox>D</TooltipKeyBox>
                      </HStack>
                    } 
                    placement="top"
                    offset={[0, -35]}
                  >
                    <IconButton
                      aria-label="Add from Dropbox"
                      icon={<FaDropbox size={80} />}
                      onClick={handleDropboxSelectFromModal}
                      colorScheme="blue"
                      variant="ghost"
                      size="lg"
                      width="150px"
                      height="150px"
                      borderRadius="2xl"
                      _hover={{
                        transform: 'scale(1.05)',
                        borderColor: 'transparent'
                      }}
                      _active={{
                        transform: 'scale(0.95)',
                        borderColor: 'transparent'
                      }}
                    />
                  </Tooltip>

                  {!isMobileDevice() && (
                    <Tooltip 
                      label={
                        <HStack spacing={2} color="gray.800">
                          <Text>Add public download links from forums, websites or Dropbox</Text>
                          <TooltipKeyBox>F</TooltipKeyBox>
                        </HStack>
                      } 
                      placement="top"
                      offset={[0, -35]}
                    >
                      <IconButton
                        aria-label="Add direct file links"
                        icon={<FiLink size={74} />}
                        onClick={handleDirectLinkSelectFromModal}
                        colorScheme="blue"
                        variant="ghost"
                        size="lg"
                        width="150px"
                        height="150px"
                        borderRadius="2xl"
                        _hover={{
                          transform: 'scale(1.05)',
                          borderColor: 'transparent'
                        }}
                        _active={{
                          transform: 'scale(0.95)',
                          borderColor: 'transparent'
                        }}
                      />
                    </Tooltip>
                  )}
                </HStack>
              </Box>
              
              {onlyCloudFiles && (
                <Text fontSize="sm" color="gray.200" textAlign="center" mt={2}>
                  Adding local files will disable setup sharing
                </Text>
              )}
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
      
      {/* Direct File Link Import Modal */}
      <DirectFileLinkImportModal
        isOpen={isDirectLinkModalOpen}
        onClose={onDirectLinkModalClose}
        onFilesSelected={onFilesSelected}
        onLoadingChange={handleDirectLinkLoadingChange}
        onProgressChange={handleDirectLinkProgressChange}
      />
    </>
  );
};

export default AddFilesButton;
